<template>
	<div class="split-area" ref="container">
		<div class="split-area-side split-area-primary ui-container" :class="{'split-area-primary-only':noSecondary}" :style="primaryStyle" ref="primaryElement"><slot name="primary"></slot></div>
		<template v-if="!noSecondary">
			<div class="vertial-splitter" @pointerenter="splitterEnter" @pointerleave="splitterLeave" @pointerdown="resizeStart" ref="splitter" @dblclick="switchMode">
				<Transition name="ttip"><span class="grab-tip" v-if="showTooltip" :style="tooltipStyle">{{ tooltip }}</span></Transition>
				<span class="vertical-grab"></span>
				<span v-if="mode !== 'no'" class="mode-switcher" :class="'mode-'+mode" @click.prevent.stop="switchMode" @pointerenter="switcherEnter" @pointerleave="switcherLeave" @pointerdown="noResize"></span>
			</div>
			<div class="split-area-side split-area-secondary ui-container" :style="secondaryStyle" ref="secondaryElement"><slot name="secondary"></slot></div>
			<Teleport to="head"><component is="style">{{ headStyle }}</component></Teleport>
		</template>
	</div>
</template>

<script setup lang="ts">
/**
 * Does horizontal or vertical splitting of the screen
 */
import {ref, computed, CSSProperties, onMounted, watch} from "vue";
import {getElementMeasure} from "@fd/helpers/DomUtils.ts";

const props = withDefaults(defineProps<{
	/** If a name is provided, ratio will be saved to local/session storage */
	name?: string;
	// Percentage of primary  area (secondary is calculated)
	ratio?: number;

	/** Pixel width when to collapse content to vertical */
	collapseAt?: number;

	/** Minimum number of percent for primary area */
	primaryMin?: number;
	/** Minimum number of percent for secondary area */
	secondaryMin?: number;
	store?: "no" | "session" | "local";
	maxHeight?: string;
	/** Secondary panel is suppressed (probably is empty) */
	noSecondary?: boolean;
	tooltip?: string;
	mode?: "no" | "columns" | "rows";
}>(), {
	name: "",
	ratio: 66.6667,
	primaryMin: 20,
	secondaryMin: 20,
	store: "no",
	collapseAt: 700,
	noSecondary: false,
	mode: "no"
});

const emit = defineEmits(['modechange', 'sizechange']);

/** True if there is not enough room show areas side-by-side and must be stacked above each other (primary first) */
const stacked = ref<boolean>(false);

const primaryPercent = ref<number>(0);
const secondaryPercent = ref<number>(0);

const container = ref<HTMLElement | null>(null);
const splitter = ref<HTMLElement | null>(null);
const primaryElement = ref<HTMLElement | null>(null);
const secondaryElement = ref<HTMLElement | null>(null);
let startClientX:number = 0;
let startContainerWidth:number = 0;
let startPrimaryPercent:number = 0;
let startSecondaryPercent:number = 0;

const primaryWidth = ref<number>(0);
const secondaryWidth = ref<number>(0);

const headStyle = computed<string>(() => {
	return `
		@container (width < ${props.collapseAt}px) {
			.split-area {
				flex-direction: column;
				align-items: stretch;
				row-gap: 16px;
			}
			.split-area > .vertial-splitter {
				display: none;
			}
		}
	`;
});

const resizingFlag = ref<boolean>(false);
const tooltipFlag = ref<boolean>(false);
const showTooltip = computed<boolean>(() => tooltipFlag.value && !resizingFlag.value && !mouseInSwitcher.value && !!props.tooltip);
const mouseInSwitcher = ref<boolean>(false);
const tooltipTop = ref<number>(0);

let tooltipTimer = 0;

const tooltipStyle = computed<CSSProperties>(() => {
	const s: CSSProperties = {};
	s.top = tooltipTop.value + "px";
	return s;
});

function splitterEnter(e:PointerEvent) {
	if (tooltipTimer) {
		clearTimeout(tooltipTimer);
		tooltipTimer = 0;
	}
	tooltipTimer = setTimeout(() => {
		if (!mouseInSwitcher.value) tooltipFlag.value = true;
		tooltipTimer = 0;
	}, 1000);
	// Calculate tooltip top
	const target = e.currentTarget as HTMLElement;
	const rect = target.getBoundingClientRect();
	tooltipTop.value = Math.round(e.clientY - rect.top) - 16;

}
function splitterLeave() {
	if (tooltipTimer) {
		clearTimeout(tooltipTimer);
		tooltipTimer = 0;
	}
	tooltipFlag.value = false;
}

function switcherEnter(e:PointerEvent) {
	mouseInSwitcher.value = true;
}
function switcherLeave(e:PointerEvent) {
	mouseInSwitcher.value = false;
}

const resizeObserver = new ResizeObserver(updateWidths);

function updateWidths() {
	if (primaryElement.value) {
		const m = getElementMeasure(primaryElement.value);
		primaryWidth.value = m.width;
	}
	if (secondaryElement.value) {
		const m = getElementMeasure(secondaryElement.value);
		secondaryWidth.value = m.width;
	}
}

watch(primaryElement, (newPrimary, oldPrimary) => {
	if (newPrimary) resizeObserver.observe(newPrimary);
	else if (oldPrimary) resizeObserver.unobserve(oldPrimary);
}, {immediate: true});



const primaryStyle = computed<CSSProperties>(() => {
	const s:CSSProperties = { };
	if (!props.noSecondary) {
		s.flexBasis = primaryPercent.value+"%";
	}
	if (props.maxHeight) s.maxHeight = props.maxHeight;
	return s;
});
const secondaryStyle = computed<CSSProperties>(() => {
	const s:CSSProperties = { };
	s.flexBasis = secondaryPercent.value+"%";
	if (props.maxHeight) s.maxHeight = props.maxHeight;
	return s;
});

function resizeStart(e:PointerEvent) {
	e.preventDefault();
	if (splitter.value) {
		resizingFlag.value = true;
		splitterLeave();
		splitter.value.addEventListener("pointermove", resize);
		splitter.value.addEventListener("pointerup", resizeEnd);
		splitter.value.setPointerCapture(e.pointerId);
		const cm = getElementMeasure(container.value);
		startContainerWidth = cm.width;
		startClientX = e.clientX;
		startPrimaryPercent = primaryPercent.value;
		startSecondaryPercent = secondaryPercent.value;
	}
}

function limitWidth():void {
	if (secondaryPercent.value < props.secondaryMin) {
		const over = props.secondaryMin - secondaryPercent.value;
		secondaryPercent.value += over;
		primaryPercent.value -= over;
	}
	if (primaryPercent.value < props.primaryMin) {
		const over = props.primaryMin - primaryPercent.value;
		primaryPercent.value += over;
		secondaryPercent.value -= over;
	}
}

function resize(e:PointerEvent) {
	e.preventDefault();
	if (splitter.value) {
		const diff = e.clientX - startClientX;
		const diffPercent = (diff / startContainerWidth) * 100;
		primaryPercent.value = startPrimaryPercent + diffPercent;
		secondaryPercent.value = startSecondaryPercent - diffPercent;
		limitWidth();
	}
}

function resizeEnd(e:PointerEvent) {
	if (splitter.value) {
		resizingFlag.value = false;
		splitter.value.removeEventListener("pointermove", resize);
		splitter.value.removeEventListener("pointerup", resizeEnd);
		splitter.value.releasePointerCapture(e.pointerId);
		// console.log("Primary   " + primaryPercent.value);
		// console.log("Secondary " + secondaryPercent.value);
		const avg = (primaryPercent.value / (primaryPercent.value + secondaryPercent.value)) * 100;
		// console.log("Average   " + avg);
		if (props.store !== "no") {
			storage().setItem("splitRatio_" + props.name, avg.toString());
		}
	}
}

function storage():Storage {
	return props.store === "local" ? localStorage : sessionStorage;
}

function noResize(e: PointerEvent) {
	e.preventDefault();
	e.stopPropagation();
}

function switchMode(e: MouseEvent) {
	emit("modechange");
}

function getPixelSize():{ primary: number, secondary: number } {
	return { primary: primaryWidth.value, secondary: secondaryWidth.value };
}

watch([primaryWidth, secondaryWidth], () => {
	emit('sizechange', getPixelSize());
});

defineExpose({ getPixelSize, primaryWidth, secondaryWidth });

onMounted(() => {
	let ratio:number;
	if (props.store === "no") ratio = props.ratio;
	else {
		const r = storage().getItem("splitRatio_"+props.name);
		if (r == null) ratio = props.ratio;
		else ratio = parseFloat(r);
	}
	const cm = getElementMeasure(container.value);
	const netContainer = cm.width - 16; // container minus gap
	const netPrimary = netContainer * ratio / 100;
	const netSecondary = netContainer * (100 - ratio) / 100;
	primaryPercent.value = (netPrimary / cm.width) * 100;
	secondaryPercent.value = (netSecondary / cm.width) * 100;
	limitWidth();
})
</script>

<style>
.split-area {
	width: 100%;
	display: flex;
	flex-direction: row;
	align-items: flex-start;
	justify-content: flex-start;
}
.vertial-splitter {
	box-sizing: border-box;
	flex: 0 0 16px;
	cursor: ew-resize;
	align-self: stretch;
	display: flex;
	flex-direction: column;
	align-items: center;
	justify-content: center;
	position: relative;
	touch-action: none;
	user-select: none;
	-webkit-user-select: none; /* Safari */
	--mode-switcher-size: 16px;

	& > .vertical-grab {
		display: inline-block;
		box-sizing: border-box;
		height: 100%;
		width: 4px;
		border-left: 1px solid var(--border-color);
		border-right: 1px solid var(--border-color);
		transition: border-color 300ms;
	}

	& > .grab-tip {
		background-color: var(--tooltip-bg);
		color: var(--tooltip-fg);
		border: 1px solid var(--tooltip-border-color);
		position: absolute;
		top: 120px;
		left: 15px;
		font-size: var(--font-size-s);
		z-index: var(--drop-z-index);
		padding: 8px;
		border-radius: var(--button-radius);
		width: 150px;
	}

	&:hover > .vertical-grab {
		border-left-color: var(--selection-bg);
		border-right-color: var(--selection-bg);
	}

	& > .mode-switcher {
		--ms-bg: var(--main-bg);
		--ms-border: var(--border-color);
		display: inline-block;
		position: absolute;
		width: var(--mode-switcher-size);
		height: var(--mode-switcher-size);
		background-color: var(--ms-bg);
		border: 1px solid var(--ms-border);
		bottom: calc(var(--mode-switcher-size) / -2);
		border-radius: 50%;
		transition: all 300ms;
		overflow: hidden;
		cursor: pointer;
		/* box-shadow: 1px 1px 10px #00000040; */

		&::before {
			content: '';
			position: absolute;
			top: 0;
		}
		&.mode-columns {
			&::before {
				left: 0;
				width: calc(4px + (100% - 4px) / 2 );
				height: 100%;
				border-right: 4px solid var(--ms-border);
			}
		}
		&.mode-rows {
			&::before {
				left: calc((100% - 4px) / 2);
				height: calc(4px + (100% - 4px) / 2 );
				width: calc(4px + (100% - 4px) / 2 );
				border-left: 4px solid var(--ms-border);
				border-bottom: 4px solid var(--ms-border);
			}
		}
	}
	&:hover > .mode-switcher {
		--ms-border: var(--selection-bg);

		&:hover {
			--ms-bg: var(--form-bg);
		}
	}
}

@media (hover: hover) {

}

@media (pointer: coarse) {
	.vertial-splitter {
		& > .mode-switcher {
			--mode-switcher-size: 20px;
		}
	}
}

.horizontal-splitter {
	box-sizing: border-box;
	flex: 0 0 16px;
	cursor: ns-resize;
	align-self: stretch;
	display: flex;
	flex-direction: row;
	align-items: center;
	justify-content: center;
	touch-action: none;
	user-select: none;
	-webkit-user-select: none; /* Safari */

	& > .horizontal-grab {
		display: inline-block;
		box-sizing: border-box;
		width: 30%;
		height: 4px;
		border-top: 1px solid var(--border-color);
		border-bottom: 1px solid var(--border-color);
		transition: border-color 300ms;
	}

	&:hover > .horizontal-grab {
		border-top-color: var(--selection-bg);
		border-bottom-color: var(--selection-bg);
	}
}

.split-area-side {
	container-type: inline-size;
	/* overflow: auto; This breaks form shadows */
	max-height: 100%;
}
.split-area-primary {
	flex-grow: 1;
	flex-shrink: 1;
}
.split-area-secondary {
	flex-grow: 1;
	flex-shrink: 1;
}

/* Tooltip transition: fade + slight upward motion */
.ttip-enter-active,
.ttip-leave-active {
	transition: opacity 0.2s ease, transform 0.2s ease;
}

.ttip-enter-from,
.ttip-leave-to {
	opacity: 0;
	transform: translateY(6px);
}

.ttip-enter-to,
.ttip-leave-from {
	opacity: 1;
	transform: translateY(0);
}

</style>